/*
 * Ambient music for the journey, designed for kids.
 *
 * Two layers, in priority order:
 *
 *  1. Real MP3 file at /audio/{category}.mp3
 *     If a royalty-free children's track is dropped at that path it loops
 *     during the mission. This is the recommended way to get the richest
 *     sound — see DEPLOY.md for suggested sources (freepd.com, etc).
 *
 *  2. Procedural music engine (this file)
 *     A handwritten song per category, with melody + bass + drums + pad
 *     and a real chord progression. Sounds like a piece of music, not
 *     "random arpeggios". Used as a graceful fallback when no MP3 is
 *     available so the experience is never silent.
 *
 * Each category has its own "song": tempo, scale, chord progression,
 * melody, bass line, and drum pattern. The engine loops it indefinitely
 * with subtle humanisation (velocity wobble) so it doesn't feel sterile.
 */

// ---- Music theory helpers ------------------------------------------------

const A4 = 440;
const noteHz = (semitones) => A4 * Math.pow(2, semitones / 12);

// Semitone offsets from A4 for each note name we use.
const N = {
  C2: -33, D2: -31, E2: -29, F2: -28, G2: -26, A2: -24, B2: -22,
  C3: -21, D3: -19, E3: -17, F3: -16, G3: -14, A3: -12, B3: -10,
  C4: -9,  D4: -7,  E4: -5,  F4: -4,  G4: -2,  A4: 0,   B4: 2,
  C5: 3,   D5: 5,   E5: 7,   F5: 8,   G5: 10,  A5: 12,  B5: 14,
  C6: 15,  D6: 17,  E6: 19,
};

// A song: tempo, beats-per-bar, melody/bass/drums patterns. Patterns are
// arrays of { time: stepIndex (in 16ths), ...payload }, looped every `bars`.
// melody.notes  : { time, note (semitones-from-A4), dur (beats), vel }
// bass.notes    : same
// drums.kick    : list of step indices
// drums.snare   : list of step indices
// drums.hat     : list of step indices
// pad.chords    : { time, notes: [...semitones], dur }
// timbres are configured per category.
//
// All songs are 4 bars (= 64 sixteenth-note steps) long and loop cleanly.

const SONGS = {

  // -- Calm : Lullaby in F major, 60 BPM ----------------------------------
  // Chord prog: F - C - Dm - Bb
  calm: {
    bpm: 60,
    timbre: { melody: "sine", bass: "sine", pad: "sine" },
    gain:   { melody: 0.18,  bass: 0.10,  pad: 0.06, drum: 0.0 },
    pad: { chords: [
      { time: 0,  dur: 16, notes: [N.F3, N.A3, N.C4] }, // F
      { time: 16, dur: 16, notes: [N.C3, N.E3, N.G3] }, // C
      { time: 32, dur: 16, notes: [N.D3, N.F3, N.A3] }, // Dm
      { time: 48, dur: 16, notes: [N.B2, N.D3, N.F3] }, // Bb
    ]},
    bass: { notes: [
      { time: 0,  dur: 8, note: N.F2 }, { time: 8,  dur: 8, note: N.F2 },
      { time: 16, dur: 8, note: N.C3 }, { time: 24, dur: 8, note: N.C3 },
      { time: 32, dur: 8, note: N.D3 }, { time: 40, dur: 8, note: N.D3 },
      { time: 48, dur: 8, note: N.B2 }, { time: 56, dur: 8, note: N.B2 },
    ]},
    melody: { notes: [
      // bar 1 (F): F4 .. A4 - C5 .. A4
      { time: 0,  dur: 3, note: N.F4 }, { time: 4,  dur: 2, note: N.A4 },
      { time: 8,  dur: 3, note: N.C5 }, { time: 12, dur: 3, note: N.A4 },
      // bar 2 (C): G4 .. E5 - C5 .. E4
      { time: 16, dur: 3, note: N.G4 }, { time: 20, dur: 2, note: N.E5 },
      { time: 24, dur: 3, note: N.C5 }, { time: 28, dur: 3, note: N.E4 },
      // bar 3 (Dm): F4 .. A4 - D5 .. A4
      { time: 32, dur: 3, note: N.F4 }, { time: 36, dur: 2, note: N.A4 },
      { time: 40, dur: 3, note: N.D5 }, { time: 44, dur: 3, note: N.A4 },
      // bar 4 (Bb): D4 .. F4 - Bb4 .. F4 (resolve)
      { time: 48, dur: 3, note: N.D4 }, { time: 52, dur: 2, note: N.F4 },
      { time: 56, dur: 6, note: N.A4 - 1 /* Bb */ },
    ]},
    drums: { kick: [], snare: [], hat: [] },
  },

  // -- Wisdom : Curious waltz-feel in C major, 92 BPM ---------------------
  // Chord prog: C - Am - F - G  (I-vi-IV-V)
  wisdom: {
    bpm: 92,
    timbre: { melody: "triangle", bass: "sine", pad: "sine" },
    gain:   { melody: 0.16, bass: 0.10, pad: 0.05, drum: 0.05 },
    pad: { chords: [
      { time: 0,  dur: 16, notes: [N.C4, N.E4, N.G4] },
      { time: 16, dur: 16, notes: [N.A3, N.C4, N.E4] },
      { time: 32, dur: 16, notes: [N.F3, N.A3, N.C4] },
      { time: 48, dur: 16, notes: [N.G3, N.B3, N.D4] },
    ]},
    bass: { notes: [
      { time: 0,  dur: 4, note: N.C3 }, { time: 4,  dur: 4, note: N.G3 },
      { time: 8,  dur: 4, note: N.C3 }, { time: 12, dur: 4, note: N.E3 },
      { time: 16, dur: 4, note: N.A2 }, { time: 20, dur: 4, note: N.E3 },
      { time: 24, dur: 4, note: N.A2 }, { time: 28, dur: 4, note: N.C3 },
      { time: 32, dur: 4, note: N.F2 }, { time: 36, dur: 4, note: N.C3 },
      { time: 40, dur: 4, note: N.F2 }, { time: 44, dur: 4, note: N.A2 },
      { time: 48, dur: 4, note: N.G2 }, { time: 52, dur: 4, note: N.D3 },
      { time: 56, dur: 4, note: N.G2 }, { time: 60, dur: 4, note: N.B2 },
    ]},
    melody: { notes: [
      // bar 1: gentle stepwise
      { time: 0, dur: 2, note: N.E4 }, { time: 2, dur: 2, note: N.G4 },
      { time: 4, dur: 2, note: N.C5 }, { time: 6, dur: 2, note: N.G4 },
      { time: 8, dur: 4, note: N.E4 }, { time: 12, dur: 4, note: N.D4 },
      // bar 2
      { time: 16, dur: 2, note: N.C4 }, { time: 18, dur: 2, note: N.E4 },
      { time: 20, dur: 2, note: N.A4 }, { time: 22, dur: 2, note: N.E4 },
      { time: 24, dur: 4, note: N.C4 }, { time: 28, dur: 4, note: N.B3 },
      // bar 3
      { time: 32, dur: 2, note: N.A3 }, { time: 34, dur: 2, note: N.C4 },
      { time: 36, dur: 2, note: N.F4 }, { time: 38, dur: 2, note: N.C4 },
      { time: 40, dur: 4, note: N.A3 }, { time: 44, dur: 4, note: N.F4 },
      // bar 4
      { time: 48, dur: 2, note: N.G3 }, { time: 50, dur: 2, note: N.B3 },
      { time: 52, dur: 2, note: N.D4 }, { time: 54, dur: 2, note: N.B3 },
      { time: 56, dur: 8, note: N.G3 },
    ]},
    drums: { kick: [0, 32], snare: [], hat: [8, 24, 40, 56] },
  },

  // -- Creative : Playful pizzicato in G major, 110 BPM -------------------
  // Chord prog: G - Em - C - D
  creative: {
    bpm: 110,
    timbre: { melody: "triangle", bass: "square", pad: "sine" },
    gain:   { melody: 0.15, bass: 0.10, pad: 0.04, drum: 0.08 },
    pad: { chords: [
      { time: 0,  dur: 16, notes: [N.G3, N.B3, N.D4] },
      { time: 16, dur: 16, notes: [N.E3, N.G3, N.B3] },
      { time: 32, dur: 16, notes: [N.C3, N.E3, N.G3] },
      { time: 48, dur: 16, notes: [N.D3, N.F4 + 1 /* F#3->F#4 ignore */, N.A3] },
    ]},
    bass: { notes: [
      // walking eighths root-fifth
      { time: 0,  dur: 2, note: N.G2 }, { time: 2,  dur: 2, note: N.D3 },
      { time: 4,  dur: 2, note: N.G2 }, { time: 6,  dur: 2, note: N.B2 },
      { time: 8,  dur: 2, note: N.G2 }, { time: 10, dur: 2, note: N.D3 },
      { time: 12, dur: 2, note: N.G2 }, { time: 14, dur: 2, note: N.B2 },
      { time: 16, dur: 2, note: N.E2 }, { time: 18, dur: 2, note: N.B2 },
      { time: 20, dur: 2, note: N.E2 }, { time: 22, dur: 2, note: N.G2 },
      { time: 24, dur: 2, note: N.E2 }, { time: 26, dur: 2, note: N.B2 },
      { time: 28, dur: 2, note: N.E2 }, { time: 30, dur: 2, note: N.G2 },
      { time: 32, dur: 2, note: N.C3 }, { time: 34, dur: 2, note: N.G3 },
      { time: 36, dur: 2, note: N.C3 }, { time: 38, dur: 2, note: N.E3 },
      { time: 40, dur: 2, note: N.C3 }, { time: 42, dur: 2, note: N.G3 },
      { time: 44, dur: 2, note: N.C3 }, { time: 46, dur: 2, note: N.E3 },
      { time: 48, dur: 2, note: N.D3 }, { time: 50, dur: 2, note: N.A3 },
      { time: 52, dur: 2, note: N.D3 }, { time: 54, dur: 2, note: N.F4 - 11 /* F#3 */ },
      { time: 56, dur: 2, note: N.D3 }, { time: 58, dur: 2, note: N.A3 },
      { time: 60, dur: 2, note: N.D3 }, { time: 62, dur: 2, note: N.F4 - 11 },
    ]},
    melody: { notes: [
      // bouncy pizzicato — short notes with skips
      { time: 0,  dur: 1, note: N.D5 }, { time: 2,  dur: 1, note: N.B4 },
      { time: 4,  dur: 1, note: N.G4 }, { time: 6,  dur: 1, note: N.B4 },
      { time: 8,  dur: 1, note: N.D5 }, { time: 10, dur: 1, note: N.G5 },
      { time: 12, dur: 4, note: N.D5 },
      { time: 16, dur: 1, note: N.B4 }, { time: 18, dur: 1, note: N.G4 },
      { time: 20, dur: 1, note: N.E4 }, { time: 22, dur: 1, note: N.G4 },
      { time: 24, dur: 1, note: N.B4 }, { time: 26, dur: 1, note: N.E5 },
      { time: 28, dur: 4, note: N.B4 },
      { time: 32, dur: 1, note: N.C5 }, { time: 34, dur: 1, note: N.E5 },
      { time: 36, dur: 1, note: N.G5 }, { time: 38, dur: 1, note: N.E5 },
      { time: 40, dur: 1, note: N.C5 }, { time: 42, dur: 1, note: N.G4 },
      { time: 44, dur: 4, note: N.C5 },
      { time: 48, dur: 1, note: N.D5 }, { time: 50, dur: 1, note: N.F4 + 1 /* F#5 */ + 12 - 1 /* approx */ },
      { time: 52, dur: 1, note: N.A4 }, { time: 54, dur: 1, note: N.D5 },
      { time: 56, dur: 8, note: N.G4 },
    ]},
    drums: {
      kick:  [0, 8, 16, 24, 32, 40, 48, 56],
      snare: [4, 12, 20, 28, 36, 44, 52, 60],
      hat:   [2, 6, 10, 14, 18, 22, 26, 30, 34, 38, 42, 46, 50, 54, 58, 62],
    },
  },

  // -- Daily life : Jaunty march in D major, 118 BPM ----------------------
  // Chord prog: D - G - A - D
  daily_life: {
    bpm: 118,
    timbre: { melody: "square", bass: "square", pad: "sine" },
    gain:   { melody: 0.10, bass: 0.10, pad: 0.04, drum: 0.10 },
    pad: { chords: [
      { time: 0,  dur: 16, notes: [N.D3, N.F4 - 11 /* F#3 */, N.A3] },
      { time: 16, dur: 16, notes: [N.G3, N.B3, N.D4] },
      { time: 32, dur: 16, notes: [N.A3, N.C4 + 1 /* C#4 */, N.E4] },
      { time: 48, dur: 16, notes: [N.D3, N.F4 - 11, N.A3] },
    ]},
    bass: { notes: [
      // strong oom-pah march
      { time: 0,  dur: 4, note: N.D2 }, { time: 4,  dur: 4, note: N.A2 },
      { time: 8,  dur: 4, note: N.D2 }, { time: 12, dur: 4, note: N.A2 },
      { time: 16, dur: 4, note: N.G2 }, { time: 20, dur: 4, note: N.D3 },
      { time: 24, dur: 4, note: N.G2 }, { time: 28, dur: 4, note: N.D3 },
      { time: 32, dur: 4, note: N.A2 }, { time: 36, dur: 4, note: N.E3 },
      { time: 40, dur: 4, note: N.A2 }, { time: 44, dur: 4, note: N.C4 + 1 - 12 },
      { time: 48, dur: 4, note: N.D2 }, { time: 52, dur: 4, note: N.A2 },
      { time: 56, dur: 4, note: N.D2 }, { time: 60, dur: 4, note: N.A2 },
    ]},
    melody: { notes: [
      // dotted brass-march melody
      { time: 0,  dur: 3, note: N.D4 }, { time: 3,  dur: 1, note: N.E4 },
      { time: 4,  dur: 4, note: N.F4 - 11 + 12 /* F#4 */ },
      { time: 8,  dur: 3, note: N.A4 }, { time: 11, dur: 1, note: N.F4 - 11 + 12 },
      { time: 12, dur: 4, note: N.D4 },
      { time: 16, dur: 3, note: N.G4 }, { time: 19, dur: 1, note: N.A4 },
      { time: 20, dur: 4, note: N.B4 },
      { time: 24, dur: 3, note: N.D5 }, { time: 27, dur: 1, note: N.B4 },
      { time: 28, dur: 4, note: N.G4 },
      { time: 32, dur: 3, note: N.A4 }, { time: 35, dur: 1, note: N.B4 },
      { time: 36, dur: 4, note: N.C5 + 1 /* C#5 */ },
      { time: 40, dur: 3, note: N.E5 }, { time: 43, dur: 1, note: N.C5 + 1 },
      { time: 44, dur: 4, note: N.A4 },
      { time: 48, dur: 3, note: N.D4 }, { time: 51, dur: 1, note: N.F4 - 11 + 12 },
      { time: 52, dur: 4, note: N.A4 },
      { time: 56, dur: 8, note: N.D5 },
    ]},
    drums: {
      kick:  [0, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 52, 56, 60],
      snare: [2, 6, 10, 14, 18, 22, 26, 30, 34, 38, 42, 46, 50, 54, 58, 62],
      hat:   [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30,
              32, 34, 36, 38, 40, 42, 44, 46, 48, 50, 52, 54, 56, 58, 60, 62],
    },
  },

  // -- Energy : Bouncy synth-pop in A minor, 138 BPM ----------------------
  // Chord prog: Am - F - C - G
  energy: {
    bpm: 138,
    timbre: { melody: "sawtooth", bass: "square", pad: "sawtooth" },
    gain:   { melody: 0.09, bass: 0.10, pad: 0.04, drum: 0.10 },
    pad: { chords: [
      { time: 0,  dur: 16, notes: [N.A3, N.C4, N.E4] },
      { time: 16, dur: 16, notes: [N.F3, N.A3, N.C4] },
      { time: 32, dur: 16, notes: [N.C4, N.E4, N.G4] },
      { time: 48, dur: 16, notes: [N.G3, N.B3, N.D4] },
    ]},
    bass: { notes: [
      // pumping straight eighths
      ...Array.from({ length: 32 }).map((_, i) => {
        const beat = i * 2;
        const chord = Math.floor(beat / 16);
        const root = [N.A2, N.F2, N.C3, N.G2][chord];
        return { time: beat, dur: 2, note: root };
      }),
    ]},
    melody: { notes: [
      // bar 1 (Am): A4 - C5 - E5 - C5 - A4 - E5 - hold
      { time: 0,  dur: 1, note: N.A4 }, { time: 1,  dur: 1, note: N.C5 },
      { time: 2,  dur: 1, note: N.E5 }, { time: 3,  dur: 1, note: N.C5 },
      { time: 4,  dur: 2, note: N.A4 }, { time: 6,  dur: 2, note: N.E5 },
      { time: 8,  dur: 4, note: N.C5 }, { time: 12, dur: 4, note: N.E5 },
      // bar 2 (F)
      { time: 16, dur: 1, note: N.F4 }, { time: 17, dur: 1, note: N.A4 },
      { time: 18, dur: 1, note: N.C5 }, { time: 19, dur: 1, note: N.A4 },
      { time: 20, dur: 2, note: N.F4 }, { time: 22, dur: 2, note: N.C5 },
      { time: 24, dur: 4, note: N.A4 }, { time: 28, dur: 4, note: N.F4 },
      // bar 3 (C)
      { time: 32, dur: 1, note: N.C5 }, { time: 33, dur: 1, note: N.E5 },
      { time: 34, dur: 1, note: N.G5 }, { time: 35, dur: 1, note: N.E5 },
      { time: 36, dur: 2, note: N.C5 }, { time: 38, dur: 2, note: N.G5 },
      { time: 40, dur: 4, note: N.E5 }, { time: 44, dur: 4, note: N.C5 },
      // bar 4 (G) — climb to high A then resolve
      { time: 48, dur: 1, note: N.G4 }, { time: 49, dur: 1, note: N.B4 },
      { time: 50, dur: 1, note: N.D5 }, { time: 51, dur: 1, note: N.B4 },
      { time: 52, dur: 2, note: N.G5 }, { time: 54, dur: 2, note: N.D5 },
      { time: 56, dur: 8, note: N.A4 },
    ]},
    drums: {
      // four-on-the-floor + offbeat hihats + snare on 2 & 4
      kick:  [0, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 52, 56, 60],
      snare: [4, 12, 20, 28, 36, 44, 52, 60],
      hat:   [2, 6, 10, 14, 18, 22, 26, 30, 34, 38, 42, 46, 50, 54, 58, 62],
    },
  },
};

// ---- Audio engine --------------------------------------------------------

let ctx = null;
let master = null;
let current = null; // { stop, fadeOut, audioEl? }

function ensureContext() {
  if (ctx) return ctx;
  const Ctor = typeof window !== "undefined" ? (window.AudioContext || window.webkitAudioContext) : null;
  if (!Ctor) return null;
  ctx = new Ctor();
  master = ctx.createGain();
  master.gain.value = 1.0;
  master.connect(ctx.destination);
  return ctx;
}

/**
 * One pitched note (oscillator → ADSR gain → destination).
 */
function note(ac, dest, freq, when, durSec, timbre, vel = 0.7) {
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = timbre;
  o.frequency.value = freq;
  o.connect(g);
  g.connect(dest);
  const a = 0.015;
  const d = Math.max(0.05, durSec * 0.7);
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(vel, when + a);
  g.gain.exponentialRampToValueAtTime(0.0001, when + a + d);
  o.start(when);
  o.stop(when + a + d + 0.05);
}

/**
 * Soft sustained pad (3-voice chord).
 */
function pad(ac, dest, freqs, when, durSec, timbre) {
  freqs.forEach((f) => {
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = timbre;
    o.frequency.value = f;
    o.connect(g);
    g.connect(dest);
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(1, when + 0.3);
    g.gain.linearRampToValueAtTime(1, when + durSec - 0.3);
    g.gain.linearRampToValueAtTime(0, when + durSec);
    o.start(when);
    o.stop(when + durSec + 0.1);
  });
}

// Kick: low sine sweep + click
function kick(ac, dest, when, vel = 1.0) {
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = "sine";
  o.frequency.setValueAtTime(140, when);
  o.frequency.exponentialRampToValueAtTime(45, when + 0.12);
  o.connect(g);
  g.connect(dest);
  g.gain.setValueAtTime(0.0001, when);
  g.gain.linearRampToValueAtTime(vel, when + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.18);
  o.start(when);
  o.stop(when + 0.2);
}

// Noise buffer cache
let noiseBuf = null;
function noise(ac) {
  if (noiseBuf) return noiseBuf;
  const len = ac.sampleRate * 1;
  noiseBuf = ac.createBuffer(1, len, ac.sampleRate);
  const data = noiseBuf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return noiseBuf;
}

// Snare: filtered noise burst with a tiny tonal click
function snare(ac, dest, when, vel = 0.6) {
  const src = ac.createBufferSource();
  src.buffer = noise(ac);
  const bp = ac.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 1800;
  bp.Q.value = 0.9;
  const g = ac.createGain();
  src.connect(bp); bp.connect(g); g.connect(dest);
  g.gain.setValueAtTime(0.0001, when);
  g.gain.linearRampToValueAtTime(vel, when + 0.003);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.15);
  src.start(when);
  src.stop(when + 0.16);
}

// Hihat: high-passed noise tick
function hat(ac, dest, when, vel = 0.25) {
  const src = ac.createBufferSource();
  src.buffer = noise(ac);
  const hp = ac.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 7500;
  const g = ac.createGain();
  src.connect(hp); hp.connect(g); g.connect(dest);
  g.gain.setValueAtTime(0.0001, when);
  g.gain.linearRampToValueAtTime(vel, when + 0.002);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
  src.start(when);
  src.stop(when + 0.06);
}

/**
 * Schedule one bar (16 sixteenth-steps) starting at `when`.
 */
function scheduleBar(ac, buses, song, when) {
  const sec16 = 60 / song.bpm / 4; // duration of one 16th-step in seconds
  const beat = 60 / song.bpm;

  song.pad.chords.forEach((c) => {
    const t = when + c.time * sec16;
    const dur = c.dur * sec16;
    pad(ac, buses.pad, c.notes.map(noteHz), t, dur, song.timbre.pad);
  });

  song.bass.notes.forEach((b) => {
    const t = when + b.time * sec16;
    const dur = b.dur * beat / 4; // note `dur` is in beats? no, in 16ths
    const seconds = b.dur * sec16;
    note(ac, buses.bass, noteHz(b.note), t, seconds, song.timbre.bass, 0.8);
  });

  song.melody.notes.forEach((m) => {
    const t = when + m.time * sec16;
    const seconds = m.dur * sec16;
    const vel = 0.7 + (Math.random() - 0.5) * 0.1;
    note(ac, buses.melody, noteHz(m.note), t, seconds, song.timbre.melody, vel);
  });

  song.drums.kick.forEach((s)  => kick(ac,  buses.drum, when + s * sec16, 0.9));
  song.drums.snare.forEach((s) => snare(ac, buses.drum, when + s * sec16, 0.55));
  song.drums.hat.forEach((s)   => hat(ac,   buses.drum, when + s * sec16, 0.25));
}

function startProcedural(category) {
  const ac = ensureContext();
  if (!ac) return null;
  if (ac.state === "suspended") ac.resume();

  const song = SONGS[category] || SONGS.calm;

  // Per-instrument buses so we can mix them.
  const buses = {};
  for (const k of ["pad", "bass", "melody", "drum"]) {
    const g = ac.createGain();
    g.gain.value = 0;
    g.connect(master);
    g.gain.linearRampToValueAtTime((song.gain[k] ?? 0.1), ac.currentTime + 1.2);
    buses[k] = g;
  }

  const sec16 = 60 / song.bpm / 4;
  const barSec = 64 * sec16;
  let nextBarAt = ac.currentTime + 0.1;
  let stopped = false;

  const scheduleAhead = () => {
    if (stopped) return;
    const horizon = ac.currentTime + 4; // 4 seconds of lookahead
    while (nextBarAt < horizon) {
      scheduleBar(ac, buses, song, nextBarAt);
      nextBarAt += barSec;
    }
    setTimeout(scheduleAhead, 1500);
  };
  scheduleAhead();

  return {
    fadeOut: () => {
      stopped = true;
      const t0 = ac.currentTime;
      for (const k of Object.keys(buses)) {
        const g = buses[k];
        g.gain.cancelScheduledValues(t0);
        g.gain.setValueAtTime(g.gain.value, t0);
        g.gain.linearRampToValueAtTime(0, t0 + 1.0);
      }
      setTimeout(() => {
        for (const k of Object.keys(buses)) buses[k].disconnect();
      }, 1300);
    },
  };
}

/**
 * Try to play a real MP3 from /audio/{category}.mp3. If it 404s or fails
 * to load, fall back to the procedural engine.
 */
function tryStartFile(category) {
  const ac = ensureContext();
  if (!ac || typeof Audio === "undefined") return null;

  const el = new Audio(`/audio/${category}.mp3`);
  el.loop = true;
  el.volume = 0.7;
  el.preload = "auto";

  let started = false;
  el.addEventListener("canplay", () => {
    if (started) return;
    started = true;
    el.play().catch(() => {});
  });
  el.addEventListener("error", () => { /* fall back handled by caller via promise */ });

  return el;
}

// ---- Public API ----------------------------------------------------------

function startFileTrack(url) {
  const el = new Audio(url);
  el.loop = true;
  el.volume = 0.7;
  el.preload = "auto";
  el.play().catch(() => {});
  return {
    fadeOut: () => {
      const fade = () => {
        if (el.volume > 0.05) {
          el.volume = Math.max(0, el.volume - 0.05);
          setTimeout(fade, 40);
        } else {
          el.pause(); el.src = "";
        }
      };
      fade();
    },
  };
}

/**
 * Start ambient music for a mission.
 * Priority chain:
 *   1. explicit `missionUrl` (per-mission MP3 uploaded by the admin)
 *   2. /audio/{category}.mp3 (category-level MP3 uploaded by the admin)
 *   3. procedural engine (always-available fallback)
 */
export function startAmbient(category, missionUrl = null) {
  ensureContext();
  if (current) {
    try { current.fadeOut(); } catch (_) {}
    current = null;
  }

  if (missionUrl) {
    current = startFileTrack(missionUrl);
    return;
  }

  fetch(`/audio/${category}.mp3`, { method: "HEAD" })
    .then((r) => {
      if (current) return;
      // Trust the response only if the server says it's actually audio —
      // some setups (SPA fallback, CDN error pages) return 200 with HTML
      // and we don't want the <audio> element to choke on that.
      const ct = (r.headers.get("content-type") || "").toLowerCase();
      if (r.ok && ct.startsWith("audio/")) {
        current = startFileTrack(`/audio/${category}.mp3`);
        return;
      }
      current = startProcedural(category);
    })
    .catch(() => {
      current = startProcedural(category);
    });
}

export function stopAmbient() {
  if (current) {
    try { current.fadeOut(); } catch (_) {}
    current = null;
  }
}

/**
 * Rising three-note ding — kept from the old module so the rest of the
 * app keeps working unchanged.
 */
export function playStarDing(stars = 3) {
  const ac = ensureContext();
  if (!ac) return;
  if (ac.state === "suspended") ac.resume();

  const t0 = ac.currentTime;
  const semis = [3, 7, 10].slice(0, Math.max(1, stars)); // C5, E5, G5 above A4
  semis.forEach((s, i) => {
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = "triangle";
    o.frequency.value = noteHz(s);
    o.connect(g);
    g.connect(master || ac.destination);
    const when = t0 + i * 0.13;
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(0.22, when + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, when + 0.5);
    o.start(when);
    o.stop(when + 0.55);
  });
}
